/**
 * GLSL ES 3.00 shader sources for the WebGL2 Mertens-fusion backend.
 *
 * Each fragment shader is paired with the same full-screen-triangle vertex
 * shader (no VBO needed — gl_VertexID drives a 3-vertex covering triangle).
 *
 * Numerical contract — must match `src/pipeline/hdr/mertens.js` exactly:
 *
 *   - 5-tap binomial kernel [1,4,6,4,1]/16, separable horizontal then vertical
 *   - Boundary: CLAMP via texelFetch with manual clamp to [0, dim-1]; sampler
 *     CLAMP_TO_EDGE alone would be enough but we use texelFetch + ivec to avoid
 *     any interpolation drift (RGBA32F sampling MAY be filtered nearest-only on
 *     some drivers; texelFetch is unambiguous integer addressing)
 *   - Downsample: stride-2 nearest pick AFTER the blur, expressed as a 5-tap
 *     read on the horiz-blurred temp at `(2*dx + j - 2)` with clamp
 *   - Upsample: zero-insert at even output positions, source values scaled ×4
 *     to compensate for missing energy, then 5-tap blur
 *   - Weights = (contrast+EPS)^a · (sat+EPS)^b · (wexp+EPS)^c
 *     where contrast = |Laplacian(luminance Rec.709)|, sat = stddev across RGB,
 *     wexp = product per-channel exp(-(c-μ)^2 / (2σ^2)) with μ=0.18, σ=0.2
 *
 * EPS is kept identical to the CPU side (1e-12) so computeWeights matches at
 * the bit level on identical inputs.
 */

/* Full-screen triangle: emits a single triangle that covers the viewport.
 * Vertex i ∈ {0,1,2} maps to ((i<<1)&2, i&2) — i.e. (0,0)(2,0)(0,2) in clip
 * before the bias, giving a triangle whose intersection with the screen
 * exactly tiles UV = [0..1]^2. No attribute buffer required. */
export const VERTEX_FULLSCREEN = /* glsl */ `#version 300 es
precision highp float;
out vec2 vUv;
void main() {
    vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
    vUv = p;
    gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}
`;

/* ── Internal GLSL helpers (concatenated into each fragment shader) ─────── */

/* Manual clamp helper. Even though sampler CLAMP_TO_EDGE handles ±2 reads
 * fine for our 5-tap kernel, using texelFetch + manual clamp keeps the math
 * identical to the CPU's `clampIdx`. */
const HELPERS = /* glsl */ `
const float BINOM0 = 0.0625;   // 1/16
const float BINOM1 = 0.25;     // 4/16
const float BINOM2 = 0.375;    // 6/16
const float BINOM3 = 0.25;     // 4/16
const float BINOM4 = 0.0625;   // 1/16

/* Reflective boundary index — mirror of CPU mertens.js:clampIdx.
 * Iterative reflect handles deep pyramid levels with very small dims;
 * for 5-tap kernels the loop terminates in ≤ 1 iteration in practice.
 * Edge-clamp left a top-row sawtooth at the deepest pyramid levels;
 * reflective gives correct DC behaviour at the boundary. */
int clampIdx(int x, int n) {
    if (n <= 1) return 0;
    /* GLSL ES 3.0 doesn't allow while with a non-constant bound on
     * every driver, but a small fixed-iteration loop is portable. <=4
     * iterations is enough for all our 5-tap kernels at any level. */
    for (int iter = 0; iter < 4; iter++) {
        if (x < 0)            x = -x;
        else if (x >= n)      x = 2 * (n - 1) - x;
        else                  break;
    }
    return x;
}
`;

/* ── Operator: horizontal 5-tap binomial blur, full size ─────────────── */
/* Used as the H-pass of the stride-2 downsample (V-pass picks stride-2). */
export const FRAG_BLUR_H = /* glsl */ `#version 300 es
precision highp float;
${HELPERS}
uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;     // (w, h) of input texture
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    vec4 c =
          BINOM0 * texelFetch(uSrc, ivec2(clampIdx(x - 2, uSrcSize.x), y), 0)
        + BINOM1 * texelFetch(uSrc, ivec2(clampIdx(x - 1, uSrcSize.x), y), 0)
        + BINOM2 * texelFetch(uSrc, ivec2(x,                          y), 0)
        + BINOM3 * texelFetch(uSrc, ivec2(clampIdx(x + 1, uSrcSize.x), y), 0)
        + BINOM4 * texelFetch(uSrc, ivec2(clampIdx(x + 2, uSrcSize.x), y), 0);
    outColor = c;
}
`;

/* ── Operator: vertical 5-tap binomial blur with stride-2 source pick ─── */
/* Renders into the (dw, dh) FBO. Source coords pre-scaled ×2 so the kernel
 * reads source at (2*dx, 2*dy ± j) — matches Lane A's `downsample1(blur1(...))`. */
export const FRAG_DOWNSAMPLE_V = /* glsl */ `#version 300 es
precision highp float;
${HELPERS}
uniform highp sampler2D uSrc;     // horiz-blurred temp at (sw, sh) full size
uniform ivec2 uSrcSize;           // (sw, sh)
uniform ivec2 uDstSize;           // (dw, dh) — render target dims
in vec2 vUv;
out vec4 outColor;
void main() {
    int dx = int(floor(vUv.x * float(uDstSize.x)));
    int dy = int(floor(vUv.y * float(uDstSize.y)));
    if (dx >= uDstSize.x) dx = uDstSize.x - 1;
    if (dy >= uDstSize.y) dy = uDstSize.y - 1;
    int sx = clampIdx(dx * 2, uSrcSize.x);
    int sy = dy * 2;
    vec4 c =
          BINOM0 * texelFetch(uSrc, ivec2(sx, clampIdx(sy - 2, uSrcSize.y)), 0)
        + BINOM1 * texelFetch(uSrc, ivec2(sx, clampIdx(sy - 1, uSrcSize.y)), 0)
        + BINOM2 * texelFetch(uSrc, ivec2(sx, clampIdx(sy,     uSrcSize.y)), 0)
        + BINOM3 * texelFetch(uSrc, ivec2(sx, clampIdx(sy + 1, uSrcSize.y)), 0)
        + BINOM4 * texelFetch(uSrc, ivec2(sx, clampIdx(sy + 2, uSrcSize.y)), 0);
    outColor = c;
}
`;

/* ── Operator: full-size vertical blur (used in upsample second pass) ─── */
/* Same as a regular V-blur — sampling at integer dst coords on a same-size
 * source. Used as the V-pass of upsample after H-pass has zero-inserted. */
export const FRAG_BLUR_V = /* glsl */ `#version 300 es
precision highp float;
${HELPERS}
uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;     // (w, h) of input texture (= dst dims here)
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    vec4 c =
          BINOM0 * texelFetch(uSrc, ivec2(x, clampIdx(y - 2, uSrcSize.y)), 0)
        + BINOM1 * texelFetch(uSrc, ivec2(x, clampIdx(y - 1, uSrcSize.y)), 0)
        + BINOM2 * texelFetch(uSrc, ivec2(x, y),                            0)
        + BINOM3 * texelFetch(uSrc, ivec2(x, clampIdx(y + 1, uSrcSize.y)), 0)
        + BINOM4 * texelFetch(uSrc, ivec2(x, clampIdx(y + 2, uSrcSize.y)), 0);
    outColor = c;
}
`;

/* ── Operator: horizontal upsample with zero-insert + ×4 + 5-tap blur ── */
/* Renders at (dw, dh). For each output (dx, dy) the H-blur reads samples at
 * (dx-2 .. dx+2). For each j-tap we ask: is (dx + j - 2) an even index in
 * destination space? If yes, the corresponding source sample is `src[(dx+j-2)/2, dy]`
 * scaled ×4. If odd, it's a zero-inserted slot (no contribution). After the
 * H-pass we still need a V-pass — done by FRAG_BLUR_V at the same (dw, dh). */
export const FRAG_UPSAMPLE_H = /* glsl */ `#version 300 es
precision highp float;
${HELPERS}
uniform highp sampler2D uSrc;     // coarser source at (sw, sh)
uniform ivec2 uSrcSize;           // (sw, sh)
uniform ivec2 uDstSize;           // (dw, dh) — caller-specified output dims
in vec2 vUv;
out vec4 outColor;

vec4 sampleZeroInsertH(int dx_t, int dy) {
    /* dx_t is the destination x coordinate for a particular kernel tap.
     * Even → corresponds to a real source sample; odd → zero.
     * Reflective boundary (mirror of CPU upsample1's blur1 → clampIdx). */
    dx_t = clampIdx(dx_t, uDstSize.x);
    if ((dx_t & 1) != 0) return vec4(0.0);
    int sx = clampIdx(dx_t / 2, uSrcSize.x);
    int sy = clampIdx(dy,       uSrcSize.y);
    /* ×4 DC compensation: matches CPU upsample{1,4} multiplicand. */
    return texelFetch(uSrc, ivec2(sx, sy), 0) * 4.0;
}

void main() {
    int dx = int(floor(vUv.x * float(uDstSize.x)));
    int dy = int(floor(vUv.y * float(uDstSize.y)));
    if (dx >= uDstSize.x) dx = uDstSize.x - 1;
    if (dy >= uDstSize.y) dy = uDstSize.y - 1;
    /* Map dy from dst-space into the H-blur's source row. The H-pass works on
     * the zero-inserted "expanded" buffer; for vertical, both dst and source
     * indices are same (dy is the dst row, picked into the expanded row at
     * even=dy, odd=zero — but H-pass only reads horizontally so per-row we
     * fetch the (dy/2) source row when dy even else write zero — matching
     * CPU upsample which writes the expanded buffer at (dy*dstW + dx) for
     * dy even). Actually the CPU code zero-inserts in BOTH axes BEFORE either
     * blur runs. So the H-pass here must skip rows where dy is odd. */
    if ((dy & 1) != 0) {
        outColor = vec4(0.0);
        return;
    }
    int sy = clampIdx(dy / 2, uSrcSize.y);
    vec4 c =
          BINOM0 * sampleZeroInsertH(dx - 2, sy)
        + BINOM1 * sampleZeroInsertH(dx - 1, sy)
        + BINOM2 * sampleZeroInsertH(dx,     sy)
        + BINOM3 * sampleZeroInsertH(dx + 1, sy)
        + BINOM4 * sampleZeroInsertH(dx + 2, sy);
    outColor = c;
}
`;

/* ── Operator: per-pixel weights (contrast · saturation · well-exposedness) ─
 * Output: single-channel R32F. Read by additive accumulation pass + by the
 * normalized-weight divide pass.
 *
 * Codex R1 lock-in (mirrors CPU mertens.js):
 *   - well-exposedness uses log-luminance Gaussian about μ_EV (= log2(0.18)),
 *     not per-channel linear-domain product. Kills mid-bracket dominance.
 *   - WEIGHT_FLOOR (1e-4) replaces the prior 1e-12 EPS so flat regions
 *     don't underflow the normalize EPS protection (same fix as CPU). */
export const FRAG_WEIGHTS = /* glsl */ `#version 300 es
precision highp float;
${HELPERS}
uniform highp sampler2D uSrc;     // RGBA32F bracket at L0 (sw, sh)
uniform ivec2 uSrcSize;
uniform float uContrast;          // exponent α
uniform float uSaturation;        // exponent β
uniform float uExposure;          // exponent γ
uniform float uMuEV;              // log2(0.18) default ≈ -2.4739
uniform float uInv2SigEV2;        // 1 / (2 * σ_EV²); σ_EV in stops
uniform float uEvFloor;           // log2 floor at deep black (1e-6 default)
uniform float uWeightFloor;       // per-component floor; 1e-4 default,
                                  // 3e-4 in robust mode (Codex 2.A.2.D6)
in vec2 vUv;
out vec4 outColor;

float lumRec709(vec3 rgb) {
    return 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
}

void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;

    vec3 rgb = texelFetch(uSrc, ivec2(x, y), 0).rgb;
    float L = lumRec709(rgb);

    /* Contrast = |Laplacian(luminance)|. Edges keep contrast=0 (matches CPU). */
    float contrast = 0.0;
    if (x > 0 && x < uSrcSize.x - 1 && y > 0 && y < uSrcSize.y - 1) {
        float Ln = lumRec709(texelFetch(uSrc, ivec2(x,     y - 1), 0).rgb);
        float Ls = lumRec709(texelFetch(uSrc, ivec2(x,     y + 1), 0).rgb);
        float Lw = lumRec709(texelFetch(uSrc, ivec2(x - 1, y    ), 0).rgb);
        float Le = lumRec709(texelFetch(uSrc, ivec2(x + 1, y    ), 0).rgb);
        float lap = -Ln - Ls - Lw - Le + 4.0 * L;
        contrast = abs(lap);
    }

    /* Saturation = sqrt(mean((c - mean(rgb))²)). Same definition as CPU. */
    float m  = (rgb.r + rgb.g + rgb.b) / 3.0;
    float dr = rgb.r - m, dg = rgb.g - m, db = rgb.b - m;
    float sat = sqrt((dr * dr + dg * dg + db * db) / 3.0);

    /* Well-exposedness: log-luminance Gaussian about μ_EV (Codex R1).
     * EV_FLOOR keeps log2 finite for deep-black pixels. */
    float Lf  = max(L, uEvFloor);
    float ev  = log2(Lf);
    float dEV = ev - uMuEV;
    float wexp = exp(-(dEV * dEV) * uInv2SigEV2);

    /* Highlight saturation guard — mirror of CPU mertens.js. Damps wexp
     * smoothly to 0 as max(R,G,B) crosses [SAT_LOW, SAT_HIGH]. Restores
     * the per-channel clip-handling that the original Mertens fr·fg·fb
     * product provided implicitly. Without it the over-bracket
     * contributes at clipped highlights and produces chromatic ringing. */
    float maxC = max(rgb.r, max(rgb.g, rgb.b));
    float damp = 1.0 - smoothstep(0.95, 1.0, maxC);
    wexp *= damp;

    float wRaw = pow(contrast + uWeightFloor, uContrast)
               * pow(sat      + uWeightFloor, uSaturation)
               * pow(wexp     + uWeightFloor, uExposure);

    outColor = vec4(wRaw, 0.0, 0.0, 0.0);
}
`;

/* ── Operator: passthrough (used to seed sum_w accumulator) ────────────── */
export const FRAG_COPY_R = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    outColor = vec4(texelFetch(uSrc, ivec2(x, y), 0).r, 0.0, 0.0, 0.0);
}
`;

/* ── Guided filter (Codex R1 ringing fix) ──────────────────────────────
 *
 * He-Sun-Tang separable 1D box filter. WEBGL2 GLSL ES 3.0 doesn't always
 * accept variable loop bounds, so the loop iterates [-MAX_R, +MAX_R] with
 * an early-skip via `if (i >= -uRadius && i <= uRadius)`. Boundary uses
 * dynamic count divisor (skip out-of-bounds taps) — matches CPU exactly.
 * MAX_R must match the CPU clamp (32). */

/* Compute Rec.709 luminance of an RGBA bracket → R32F. Used to extract
 * the guide image from the chosen bracket. */
export const FRAG_LUMINANCE_R = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    vec3 rgb = texelFetch(uSrc, ivec2(x, y), 0).rgb;
    float L = 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
    outColor = vec4(L, 0.0, 0.0, 0.0);
}
`;

/* Element-wise product of two R32F textures. Used to build I*p and I*I. */
export const FRAG_PRODUCT_R = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uA;
uniform highp sampler2D uB;
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    float a = texelFetch(uA, ivec2(x, y), 0).r;
    float b = texelFetch(uB, ivec2(x, y), 0).r;
    outColor = vec4(a * b, 0.0, 0.0, 0.0);
}
`;

/* Horizontal box filter on R32F via moving-average. */
export const FRAG_BOX_H_R = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;
uniform int uRadius;
in vec2 vUv;
out vec4 outColor;
const int MAX_R = 32;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    float sum = 0.0;
    int count = 0;
    for (int i = -MAX_R; i <= MAX_R; i++) {
        if (i < -uRadius || i > uRadius) continue;
        int sx = x + i;
        if (sx < 0 || sx >= uSrcSize.x) continue;
        sum += texelFetch(uSrc, ivec2(sx, y), 0).r;
        count += 1;
    }
    float v = (count > 0) ? sum / float(count) : 0.0;
    outColor = vec4(v, 0.0, 0.0, 0.0);
}
`;

/* Vertical box filter on R32F via moving-average. */
export const FRAG_BOX_V_R = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;
uniform int uRadius;
in vec2 vUv;
out vec4 outColor;
const int MAX_R = 32;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    float sum = 0.0;
    int count = 0;
    for (int j = -MAX_R; j <= MAX_R; j++) {
        if (j < -uRadius || j > uRadius) continue;
        int sy = y + j;
        if (sy < 0 || sy >= uSrcSize.y) continue;
        sum += texelFetch(uSrc, ivec2(x, sy), 0).r;
        count += 1;
    }
    float v = (count > 0) ? sum / float(count) : 0.0;
    outColor = vec4(v, 0.0, 0.0, 0.0);
}
`;

/* Compute guided-filter (a, b) coefficients from the four moment maps,
 * packed into RG of an RGBA32F output (R=a, G=b). Textbook form:
 *   cov  = mean(I·p) − mean(I)·mean(p)
 *   varI = mean(I²)  − mean(I)²
 *   a    = cov / (varI + eps)
 *   b    = mean(p)  − a · mean(I)
 *
 * The earlier "centered" variant (cov from box-filter of pre-centered
 * products) was wrong (Codex R5): centering each sample against its own
 * local mean changes the filter, not just stabilises it. */
export const FRAG_GUIDED_AB = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uMeanI;
uniform highp sampler2D uMeanP;
uniform highp sampler2D uMeanIp;
uniform highp sampler2D uMeanII;
uniform ivec2 uSrcSize;
uniform float uEps;
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    float mI  = texelFetch(uMeanI,  ivec2(x, y), 0).r;
    float mP  = texelFetch(uMeanP,  ivec2(x, y), 0).r;
    float mIp = texelFetch(uMeanIp, ivec2(x, y), 0).r;
    float mII = texelFetch(uMeanII, ivec2(x, y), 0).r;
    float cov  = mIp - mI * mP;
    float varI = mII - mI * mI;
    float a = cov / (varI + uEps);
    float b = mP - a * mI;
    outColor = vec4(a, b, 0.0, 0.0);
}
`;

/* Final guided-filter output: q = mean(a) · I + mean(b). Inputs are the
 * guide texture and a paired (mean(a), mean(b)) RGBA32F buffer. */
export const FRAG_GUIDED_OUTPUT = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uGuide;     // R32F luminance guide
uniform highp sampler2D uMeanA;     // R32F mean(a) — single channel
uniform highp sampler2D uMeanB;     // R32F mean(b) — single channel
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    float I  = texelFetch(uGuide, ivec2(x, y), 0).r;
    float ma = texelFetch(uMeanA, ivec2(x, y), 0).r;
    float mb = texelFetch(uMeanB, ivec2(x, y), 0).r;
    outColor = vec4(ma * I + mb, 0.0, 0.0, 0.0);
}
`;

/* Extract a single channel from an RGBA32F (R or G) into an R32F.
 * Used to split the (a, b) coefficient texture for the next box filter. */
export const FRAG_EXTRACT_CHANNEL_R = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;
uniform int uChannel;   // 0 = R, 1 = G, 2 = B, 3 = A
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    vec4 c = texelFetch(uSrc, ivec2(x, y), 0);
    float v = (uChannel == 0) ? c.r
            : (uChannel == 1) ? c.g
            : (uChannel == 2) ? c.b
            : c.a;
    outColor = vec4(v, 0.0, 0.0, 0.0);
}
`;

/* ── Operator: per-bracket normalized weight = raw_w / (sum_w + EPS) ──── */
export const FRAG_NORMALIZE_W = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uRaw;     // raw weights for THIS bracket (R32F)
uniform highp sampler2D uSum;     // sum across all brackets (R32F)
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;
const float EPS = 1e-12;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    float raw = texelFetch(uRaw, ivec2(x, y), 0).r;
    float sum = texelFetch(uSum, ivec2(x, y), 0).r;
    outColor = vec4(raw / (sum + EPS), 0.0, 0.0, 0.0);
}
`;

/* ── Operator: Laplacian-diff = G_i - upsampled(G_{i+1}, dims_i) ──────── */
/* Two RGBA32F inputs at the SAME dims (next-level Gaussian already
 * upsampled to the parent's dims by a prior FRAG_UPSAMPLE_H + FRAG_BLUR_V
 * pass pair). Output: RGBA32F same dims. */
export const FRAG_LAPLACIAN_DIFF = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uG;       // G_i (parent Gaussian)
uniform highp sampler2D uUpNext;  // upsampled(G_{i+1}) at parent dims
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    vec4 g  = texelFetch(uG,      ivec2(x, y), 0);
    vec4 up = texelFetch(uUpNext, ivec2(x, y), 0);
    outColor = g - up;
}
`;

/* ── Operator: weighted accumulate = lap_rgba × w_scalar ──────────────── */
/* Used with gl.BLEND ONE/ONE so the merged pyramid level accumulates all
 * brackets' contributions. */
export const FRAG_WEIGHTED_LAP = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uLap;     // Laplacian RGBA at this level (RGBA32F)
uniform highp sampler2D uWeight;  // Gaussian-of-weight at this level (R32F)
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    vec4 lap = texelFetch(uLap,    ivec2(x, y), 0);
    float w  = texelFetch(uWeight, ivec2(x, y), 0).r;
    outColor = lap * w;
}
`;

/* ── Operator: collapse-add = current + upsample(prev_collapsed) ──────── */
/* Inputs: this level's merged Laplacian and the already-upsampled coarser
 * accumulator (passed through FRAG_UPSAMPLE_H + FRAG_BLUR_V). Output is the
 * collapsed result for this level — fed into the next iteration as the
 * "coarser accumulator" for level-1. */
export const FRAG_COLLAPSE_ADD = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uMerged;     // merged Laplacian at this level
uniform highp sampler2D uUpAccum;    // upsampled coarser accumulator at this level
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    vec4 m = texelFetch(uMerged,  ivec2(x, y), 0);
    vec4 u = texelFetch(uUpAccum, ivec2(x, y), 0);
    outColor = m + u;
}
`;

/* ── Operator: force alpha=1 on final L0 (applied just before readback) ── */
export const FRAG_FINALIZE = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    vec4 c = texelFetch(uSrc, ivec2(x, y), 0);
    outColor = vec4(c.rgb, 1.0);
}
`;

/* Exposed bundle for callers that prefer destructured imports. */
export const SHADERS = {
    VERTEX_FULLSCREEN,
    FRAG_BLUR_H,
    FRAG_DOWNSAMPLE_V,
    FRAG_BLUR_V,
    FRAG_UPSAMPLE_H,
    FRAG_WEIGHTS,
    FRAG_LUMINANCE_R,
    FRAG_PRODUCT_R,
    FRAG_BOX_H_R,
    FRAG_BOX_V_R,
    FRAG_GUIDED_AB,
    FRAG_GUIDED_OUTPUT,
    FRAG_EXTRACT_CHANNEL_R,
    FRAG_COPY_R,
    FRAG_NORMALIZE_W,
    FRAG_LAPLACIAN_DIFF,
    FRAG_WEIGHTED_LAP,
    FRAG_COLLAPSE_ADD,
    FRAG_FINALIZE,
};

/* ── Phase 2.A.3 — robust-mode hybrid base-level median blend ─────────
 *
 * Three shaders that mirror CPU mertens.js's median + mix injection at
 * the coarsest pyramid level. All three fetch up to MAX_N=8 brackets
 * via 8 sampler uniforms + a uN runtime count — within standard
 * texture-image-unit limits and portable across WebGL2 implementations.
 *
 * Even-N convention: lower-median = `sorted[(N-1)/2]` (Codex 2.A.2.design
 * D5 lock-in for CPU↔GPU parity on ties). */

/* MEDIAN of N RGBA brackets, alpha forced to 1.0 (matches CPU
 * pixelMedianRGBA convention). Selection-sort up to the lower-median
 * index is enough — we never need a fully-sorted array. Per channel.
 * N=3 fast-path uses sort-net for byte-parity with median-stack.js. */
export const FRAG_MEDIAN_RGBA = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uT0;
uniform highp sampler2D uT1;
uniform highp sampler2D uT2;
uniform highp sampler2D uT3;
uniform highp sampler2D uT4;
uniform highp sampler2D uT5;
uniform highp sampler2D uT6;
uniform highp sampler2D uT7;
uniform int uN;
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;

#define MAX_N 8

vec4 fetchBracket(int k, ivec2 p) {
    if (k == 0) return texelFetch(uT0, p, 0);
    if (k == 1) return texelFetch(uT1, p, 0);
    if (k == 2) return texelFetch(uT2, p, 0);
    if (k == 3) return texelFetch(uT3, p, 0);
    if (k == 4) return texelFetch(uT4, p, 0);
    if (k == 5) return texelFetch(uT5, p, 0);
    if (k == 6) return texelFetch(uT6, p, 0);
    return texelFetch(uT7, p, 0);
}

float medianN(float vals[MAX_N], int n) {
    int target = (n - 1) / 2;
    /* Selection-sort up to target+1 elements — O(target * n). */
    for (int i = 0; i <= target; i++) {
        int mi = i;
        for (int j = i + 1; j < MAX_N; j++) {
            if (j >= n) break;
            if (vals[j] < vals[mi]) mi = j;
        }
        if (mi != i) {
            float t = vals[i]; vals[i] = vals[mi]; vals[mi] = t;
        }
    }
    return vals[target];
}

void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    ivec2 p = ivec2(x, y);
    int n = uN;
    if (n < 1) n = 1;
    if (n > MAX_N) n = MAX_N;

    /* Special-case N=3 sort-net for byte-parity with CPU median3(). */
    if (n == 3) {
        vec4 a = fetchBracket(0, p), b = fetchBracket(1, p), c = fetchBracket(2, p);
        outColor.r = max(min(a.r, b.r), min(max(a.r, b.r), c.r));
        outColor.g = max(min(a.g, b.g), min(max(a.g, b.g), c.g));
        outColor.b = max(min(a.b, b.b), min(max(a.b, b.b), c.b));
        outColor.a = 1.0;
        return;
    }

    float r[MAX_N];
    float g[MAX_N];
    float b[MAX_N];
    for (int k = 0; k < MAX_N; k++) {
        if (k < n) {
            vec4 c = fetchBracket(k, p);
            r[k] = c.r; g[k] = c.g; b[k] = c.b;
        } else {
            r[k] = 0.0; g[k] = 0.0; b[k] = 0.0;
        }
    }
    outColor.r = medianN(r, n);
    outColor.g = medianN(g, n);
    outColor.b = medianN(b, n);
    outColor.a = 1.0;
}
`;

/* MAX of N R32F weight maps. Used to compute the hybrid mix factor. */
export const FRAG_MAXW_R = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uW0;
uniform highp sampler2D uW1;
uniform highp sampler2D uW2;
uniform highp sampler2D uW3;
uniform highp sampler2D uW4;
uniform highp sampler2D uW5;
uniform highp sampler2D uW6;
uniform highp sampler2D uW7;
uniform int uN;
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;

#define MAX_N 8

float fetchW(int k, ivec2 p) {
    if (k == 0) return texelFetch(uW0, p, 0).r;
    if (k == 1) return texelFetch(uW1, p, 0).r;
    if (k == 2) return texelFetch(uW2, p, 0).r;
    if (k == 3) return texelFetch(uW3, p, 0).r;
    if (k == 4) return texelFetch(uW4, p, 0).r;
    if (k == 5) return texelFetch(uW5, p, 0).r;
    if (k == 6) return texelFetch(uW6, p, 0).r;
    return texelFetch(uW7, p, 0).r;
}

void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    ivec2 p = ivec2(x, y);
    int n = uN;
    if (n < 1) n = 1;
    if (n > MAX_N) n = MAX_N;
    float maxW = -3.4e38;
    for (int k = 0; k < MAX_N; k++) {
        if (k >= n) break;
        float v = fetchW(k, p);
        if (v > maxW) maxW = v;
    }
    outColor = vec4(maxW, 0.0, 0.0, 0.0);
}
`;

/* HYBRID TOP — final = lerp(soft, median, mix) where
 *   mix = clamp((1 - maxW) / (1 - 1/N), 0, 1).
 * Mirrors median-stack.js:applyHybridBlendInPlace. Alpha forced to 1. */
export const FRAG_HYBRID_TOP = /* glsl */ `#version 300 es
precision highp float;
uniform highp sampler2D uSoft;
uniform highp sampler2D uMedian;
uniform highp sampler2D uMaxW;
uniform int uN;
uniform ivec2 uSrcSize;
in vec2 vUv;
out vec4 outColor;
void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;
    ivec2 p = ivec2(x, y);
    vec4 soft = texelFetch(uSoft,   p, 0);
    vec4 med  = texelFetch(uMedian, p, 0);
    float maxW = texelFetch(uMaxW,  p, 0).r;
    int n = uN;
    if (n < 2) {
        outColor = vec4(soft.rgb, 1.0);
        return;
    }
    float denom = 1.0 - 1.0 / float(n);
    float mixVal = (1.0 - maxW) / denom;
    if (mixVal < 0.0) mixVal = 0.0;
    if (mixVal > 1.0) mixVal = 1.0;
    vec3 outRgb = mix(soft.rgb, med.rgb, mixVal);
    outColor = vec4(outRgb, 1.0);
}
`;

/* Re-export the robust-mode trio in the SHADERS bundle. Done after the
 * `export const` definitions to avoid the temporal-dead-zone error
 * that hits when the bundle is constructed at module-init time. */
SHADERS.FRAG_MEDIAN_RGBA = FRAG_MEDIAN_RGBA;
SHADERS.FRAG_MAXW_R      = FRAG_MAXW_R;
SHADERS.FRAG_HYBRID_TOP  = FRAG_HYBRID_TOP;
